import React, { useEffect, useState } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
  Paper,
  Grid,
} from "@mui/material";
import { lighten, Theme } from "@mui/material/styles";

import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';

import { PurchaseOrder } from "../../../models/purchase.order";
import { GetTopIncomingPO } from "../ApiServices/serviceCalls";
import {
  lightGray,
  pprBorderColorA,
  strongGray,
  textColor,
  textGrayH1,
  toolbarBG1,
  white,
} from "../DashStyleGuides";

interface Data {
  supplier: string;
  qty: number;
  status: string;
  orderPO: number;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      height: 300,
      borderRadius: 12,
    },
    paper: {
      width: "100%",
      height: "100%",
      marginBottom: theme.spacing(2),
      border: `1px solid ${pprBorderColorA}`,
      borderRadius: 12,
      "& .MuiTableContainer-root": {
        maxHeight: 250,
      },
    },
    table: {
      minWidth: 352,
      "& .tblrow": {
        height: 44,
        fontSize: 12,
        fontFamily: "Poppins",
        fontWeight: 400,
        color: textColor,
      },
      "& .light": {
        background: white,
      },
      "& .darker": { background: lightGray },
    },
    visuallyHidden: {
      border: 0,
      clip: "rect(0 0 0 0)",
      height: 1,
      margin: -1,
      overflow: "hidden",
      padding: 0,
      position: "absolute",
      top: 20,
      width: 1,
    },
    tblPage: {
      maxWidth: 352,
      "& .MuiToolbar-gutters": { padding: 0 },
    },
    tblHeaderRoot: {
      height: 32,
      "& .MuiTableCell-stickyHeader": {
        background: toolbarBG1,
      },

      "& th": {
        fontSize: 12,
        fontWeight: 700,
        color: textGrayH1,
      },
    },
    lblEmpty: {
      display: "block",
      margin: "auto",
      marginTop: 80,
      color: pprBorderColorA,
      fontWeight: 400,
    },
  })
);

const useToolbarStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
      height: 34,
      borderBottom: `1px solid ${pprBorderColorA}`,
      borderRadius: "12px 12px 0px 0px",
    },
    highlight:
      theme.palette.mode === "light"
        ? {
            color: theme.palette.secondary.main,
            backgroundColor: lighten(theme.palette.secondary.light, 0.85),
          }
        : {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.secondary.dark,
          },
    title: {
      flex: "1 1 100%",
      fontFamily: "Manrope",
      fontStyle: "normal",
      fontWeight: "bold",
      fontSize: 16,
      color: strongGray,
    },
  })
);

type Order = "asc" | "desc";

interface HeadCell {
  disablePadding: boolean;
  id: string;
  label: string;
  numeric: boolean;
}

const headCells: HeadCell[] = [
  {
    id: "purchase_order_id",
    numeric: true,
    disablePadding: true,
    label: "Order",
  },
  {
    id: "supplier_name",
    numeric: true,
    disablePadding: false,
    label: "Supplier",
  },
  { id: "quantity", numeric: true, disablePadding: false, label: "Quantity" },
  { id: "status", numeric: false, disablePadding: false, label: "Status" },
];

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;

  const cssClasses = useStyles();

  return (
    <TableHead className={cssClasses.tblHeaderRoot}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="center"
            padding="none"
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const EnhancedTableToolbar = () => {
  const classes = useToolbarStyles();

  return (
    <Toolbar className={classes.root} variant="dense">
      <Typography className={classes.title} id="tableTitle" component="div">
        Income Purchase Orders
      </Typography>

      {/* <Tooltip title="More options">
        <IconButton aria-label="More options">
          <MoreVertRounded />
        </IconButton>
      </Tooltip> */}
    </Toolbar>
  );
};

/** Default Functional Component */
export default function Ove() {
  const classes = useStyles();
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("supplier");

  const [topIncomingPOList, setIncomingPOList] = useState<PurchaseOrder[]>([]);

  useEffect(() => {
    getTopList();
  }, []);

  const getTopList = async () => {
    const incomingPO: any = await GetTopIncomingPO();

    if (incomingPO) {
      setIncomingPOList(incomingPO !== undefined ? incomingPO.data : undefined);
    }
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
    const isAsc = orderBy === property && order === "asc";

    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  return (
    <Grid item sm={6} md={4} xs={12}>
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <EnhancedTableToolbar />
          <TableContainer>
            <Table
              className={classes.table}
              aria-labelledby="tableTitle"
              size="small"
              aria-label="enhanced table"
              stickyHeader
            >
              <EnhancedTableHead
                classes={classes}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {topIncomingPOList.length > 0 &&
                  topIncomingPOList.map((info, index) => (
                    <TableRow
                      className={`tblrow ${index % 2 == 0 ? "light" : "darker"}`}
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={index}
                    >
                      <TableCell
                        id={info?.id?.toString()}
                        scope="row"
                        padding="none"
                        align="center"
                      >
                        {info?.reference !== null ? info.reference : "-"}
                      </TableCell>
                      <TableCell scope="row" padding="none" align="center">
                        {info?.supplier?.name}
                      </TableCell>
                      <TableCell scope="row" padding="none" align="center">
                        {info?.purchaseOrderProducts && info?.purchaseOrderProducts?.length > 0
                          ? info?.purchaseOrderProducts[0]?.quantity
                          : "-"}
                      </TableCell>
                      <TableCell align="center">
                        {info?.performancePrediction !== null ? info.performancePrediction : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          {topIncomingPOList.length === 0 && (
            <Typography className={classes.lblEmpty} variant="h4" align="center">
              No Data
            </Typography>
          )}
        </Paper>
      </div>
    </Grid>
  );
}
